import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  handleFirstImage,
  handleNavigate,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import PostTemplate from "../CommonComponents/PostTemplate";
import {
  TRENDING_MAX_COUNT_DEFAULT,
  useLoadTrendingProductsMaxCount,
} from "../../../../Utils/customHooks/UpToDateContent/useLoadTrendingProductsMaxCount";
import { trendingClickSource } from "../../../../Utils/customHooks/UpToDateContent/trendingDailySnapshot";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";
import { normalizeTrendingDetails } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorTrendingDetails";

function postIsShoppable(post: any): boolean {
  const link =
    typeof post?.shop_link === "string"
      ? post.shop_link.trim()
      : typeof post?.shopLink === "string"
        ? post.shopLink.trim()
        : "";
  return (
    post?.is_shoppable === true ||
    post?.isShoppable === true ||
    Boolean(link)
  );
}

const TrendingProductsDataFront = (props: any) => {
  const itemsPerPage = 3;
  const {
    user_id,
    username,
    title,
    postIds,
    handleRedirect,
    brand_color,
    isLinks,
    component,
    type,
    details,
    removedPostIds,
    RenderUI,
    from,
    templateGridLoaderProps,
    designTokens,
    onFeedResolved,
    showEmptyInEditor = false,
  } = props;

  const param = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  // Preset tabs (7/30/90/all) store absolute dates that go stale — roll to
  // "now" like the dashboard so public LiB matches Stay Up To Date trending.
  const detailParts = useMemo(
    () => normalizeTrendingDetails(typeof details === "string" ? details : null).split(","),
    [details],
  );

  const snapshotPersist = useMemo(
    () => ({
      componentId: component?.id,
      from,
      query: component?.query,
      postIds: component?.post_ids,
    }),
    [component?.id, component?.query, component?.post_ids, from],
  );
  const hasSnapshotIds = Boolean(
    String(component?.post_ids || "")
      .split(",")
      .map((id: string) => id.trim())
      .filter(Boolean).length,
  );
  // Website LIB editor used to always bypass snapshots (slow + wrong source).
  // Prefer create-time / Update-time post_ids; only live-recompute when empty.
  const bypassSnapshot = Boolean(showEmptyInEditor && !hasSnapshotIds);
  const clickSource = useMemo(() => {
    const resolved = trendingClickSource({
      from,
      type,
      query: component?.query,
    });
    if (resolved !== "legacy") return resolved;
    // Website LIB editor (`showEmptyInEditor`) creates via Master Template with
    // template-shop/content analytics — don't fall back to legacy clicks.
    if (!showEmptyInEditor) return resolved;
    const t = String(type || "").toLowerCase();
    if (t.includes("product")) return "template-shop";
    if (t.includes("content")) return "template-content";
    return resolved;
  }, [from, type, component?.query, showEmptyInEditor]);

  const {
    isLoading: TrendingProductsLoading,
    isFetching: TrendingProductsFetching,
    isFetched: TrendingProductsFetched,
    data: TrendingProducts,
  }: any = useLoadTrendingProductsMaxCount(
      user_id,
      username,
      title,
      detailParts[0],
      removedPostIds,
      detailParts[1],
      detailParts[2],
      detailParts[3],
      component?.content_order || "DESC",
      type,
      component?.custom_order?.split(",").filter(Boolean) || [],
      false,
      hasSnapshotIds || !showEmptyInEditor ? snapshotPersist : undefined,
      bypassSnapshot,
      TRENDING_MAX_COUNT_DEFAULT,
      clickSource,
    );

  const feedData = useMemo(() => {
    const data = TrendingProducts?.data || [];
    const status = String(component?.shoppable_status_filter || "all")
      .toLowerCase()
      .trim();
    if (status === "all" || !Array.isArray(data)) return data;
    if (status === "shoppable") {
      return data.filter((post: any) => postIsShoppable(post));
    }
    // non-shoppable
    const isProducts = String(type || "").toLowerCase().includes("product");
    // Trending products require shop_link upstream — strict non-shoppable would
    // always empty. Keep cards; CTAs are hidden via `showShopLinks`.
    if (isProducts) return data;
    return data.filter((post: any) => !postIsShoppable(post));
  }, [TrendingProducts?.data, component?.shoppable_status_filter, type]);

  const showShopLinks = useMemo(() => {
    const status = String(component?.shoppable_status_filter || "all")
      .toLowerCase()
      .trim();
    if (status === "non-shoppable") return false;
    return Boolean(isLinks);
  }, [component?.shoppable_status_filter, isLinks]);

  useEffect(() => {
    if (TrendingProductsLoading) {
      setIsLoading(true);
      return;
    }
    let cancelled = false;
    const checkEmptyStatus = async () => {
      try {
        if (!feedData || feedData.length === 0) {
          // Editor keeps the section visible with a "No data" placeholder and may
          // be filtering live before Update — don't mark the row empty in DB here.
          if (!showEmptyInEditor && component?.id && title && user_id) {
            await updateIsEmptyStatus(title, user_id, component.id, from);
          }
        } else {
          // Paint first; persist card image off the critical path.
          if (!cancelled) setIsLoading(false);
          void handleFirstImage(
            title,
            component?.id,
            feedData[0],
            user_id,
            from,
          );
          return;
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void checkEmptyStatus();
    return () => {
      cancelled = true;
    };
  }, [
    TrendingProductsLoading,
    feedData,
    component?.id,
    title,
    user_id,
    from,
    showEmptyInEditor,
  ]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, feedData.length - itemsPerPage)
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current) {
        if (containerRef.current) {
          const scrollLeft = containerRef.current.scrollLeft;
          const itemWidth =
            containerRef.current.scrollWidth / Math.max(feedData.length, 1);
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [feedData]
  );

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth =
        containerRef.current.scrollWidth / Math.max(feedData.length, 1);
      const scrollPosition = itemWidth * currentIndex;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentIndex, feedData]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [feedData, handleScroll]);

  const feedPending =
    !TrendingProductsFetched ||
    TrendingProductsLoading ||
    isLoading ||
    (TrendingProductsFetching && feedData.length === 0);

  useEffect(() => {
    if (from !== "website-template" || !onFeedResolved) return;
    if (feedPending) return;
    onFeedResolved(feedData.length > 0);
  }, [
    from,
    onFeedResolved,
    feedPending,
    feedData.length,
  ]);

  return (
    <>
      {feedPending ? (
        <StayFeedLoadingPlaceholder
          from={from}
          component={component}
          designTokens={designTokens}
          useWebsiteLoaders={showEmptyInEditor}
        />
      ) : feedData.length === 0 ? (
        showEmptyInEditor ? (
          <FeedSectionEmptyState
            title={title}
            designTokens={designTokens}
            message={websiteEditorFeedEmptyMessage("trending-products")}
          />
        ) : null
      ) : RenderUI ? (
        <RenderUI
          data={feedData}
          currentIndex={currentIndex}
          handleNavigate={handleNavigate}
          component={component}
          handleNextPage={handleNextPage}
          handlePrevPage={handlePrevPage}
          containerRef={containerRef}
          handleRedirect={handleRedirect}
          brand_color={brand_color}
          title={title}
          type={type}
          componentType={component?.type || type}
          isLinks={showShopLinks}
          from={from}
          username={username}
          user_id={user_id}
          designTokens={designTokens}
        />
      ) : (
        <PostTemplate
          processedData={feedData}
          total_count={TrendingProducts?.total_count ?? 0}
          component={component}
          user_id={user_id}
          username={username}
          handleRedirect={handleRedirect}
          brand_color={brand_color}
          title={title}
          from={from}
          type={type}
          itemsPerPage={itemsPerPage}
          platform={type}
          pinnedPostIds={component?.pinned_posts?.split(",") || []}
          isLinks={showShopLinks}
          designTokens={designTokens}
        />
      )}
    </>
  );
};

export default TrendingProductsDataFront;
